/**
 * Optional VPS API client for reports_daily and audit_logs reads.
 * When VITE_API_BASE_URL is empty, callers use Firestore only.
 */

import { getFirebaseAuth } from './firebase';
import { config } from './config';
import { liveDataStatus, type FeedController } from './liveDataStatus';
import type { AuditLogEntry, ReportsDailySummary } from '../types';

const NOOP_CONTROLLER: FeedController = { refresh: () => {} };

export const VPS_FEED_REPORTS = 'vps_reports_daily';
export const VPS_FEED_AUDIT = 'vps_audit_logs';

export function isVpsApiEnabled(): boolean {
  return Boolean(config.apiBaseUrl?.trim());
}

/** Register Reports API feed before the daily summary fetch (Detailed reports). */
export function ensureVpsReportsFeedRegistered(): void {
  if (!isVpsApiEnabled()) return;
  liveDataStatus.setStatus(VPS_FEED_REPORTS, { state: 'connecting' }, NOOP_CONTROLLER);
}

/** Register Audit API feed when the user loads the audit log (not on every Reports visit). */
export function ensureVpsAuditFeedRegistered(): void {
  if (!isVpsApiEnabled()) return;
  liveDataStatus.setStatus(VPS_FEED_AUDIT, { state: 'connecting' }, NOOP_CONTROLLER);
}

async function getBearerToken(): Promise<string | null> {
  const user = getFirebaseAuth().currentUser;
  if (!user) return null;
  return user.getIdToken();
}

function setVpsFeed(
  name: string,
  state: 'connected' | 'offline',
  errorCode?: string,
): void {
  liveDataStatus.setStatus(
    name,
    {
      state,
      errorCode,
      lastSnapshotAt: state === 'connected' ? Date.now() : undefined,
    },
    NOOP_CONTROLLER,
  );
}

async function vpsGet<T extends { items: unknown[] }>(
  path: string,
  feedName: string,
): Promise<T | null> {
  const base = config.apiBaseUrl.replace(/\/$/, '');
  const token = await getBearerToken();
  if (!token) {
    console.warn(`[vpsApi] ${feedName}: no signed-in user, skipping VPS`);
    return null;
  }

  try {
    const res = await fetch(`${base}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      console.warn(`[vpsApi] ${feedName}: HTTP ${res.status}`);
      setVpsFeed(feedName, 'offline', String(res.status));
      return null;
    }
    const body = (await res.json()) as T;
    setVpsFeed(feedName, 'connected');
    return body;
  } catch (err) {
    const code = err instanceof Error ? err.message : 'network_error';
    console.warn(`[vpsApi] ${feedName}:`, err);
    setVpsFeed(feedName, 'offline', code);
    return null;
  }
}

export async function fetchReportsDailyFromVps(
  dateFrom: string,
  dateTo: string,
): Promise<ReportsDailySummary[] | null> {
  if (!isVpsApiEnabled()) return null;
  const q = new URLSearchParams({ from: dateFrom, to: dateTo });
  const body = await vpsGet<{ items: ReportsDailySummary[] }>(
    `/v1/reports/daily?${q}`,
    VPS_FEED_REPORTS,
  );
  return body?.items ?? null;
}

export async function fetchAuditLogsFromVps(
  startMs: number,
  endMs: number,
): Promise<AuditLogEntry[] | null> {
  if (!isVpsApiEnabled()) return null;
  const q = new URLSearchParams({
    from: String(startMs),
    to: String(endMs),
    limit: '2000',
  });
  const body = await vpsGet<{ items: AuditLogEntry[] }>(
    `/v1/audit?${q}`,
    VPS_FEED_AUDIT,
  );
  return body?.items ?? null;
}
